import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Users, 
  Mail, 
  LogOut, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  Check,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { language, t } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [memberships, setMemberships] = useState([]);
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title_de: '',
    title_it: '',
    description_de: '',
    description_it: '',
    date: '',
    location: '',
    year: new Date().getFullYear(),
    image_url: '',
  });

  // Auth header helper
  const getAuthHeaders = () => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/login`, null, {
        auth: {
          username: credentials.username,
          password: credentials.password,
        },
      });
      setIsLoggedIn(true);
      toast.success(language === 'de' ? 'Anmeldung erfolgreich' : 'Accesso riuscito');
      fetchData();
    } catch (error) {
      console.error('Login error:', error);
      toast.error(language === 'de' ? 'Ungültige Anmeldedaten' : 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data
  const fetchData = async () => {
    try {
      const [eventsRes, contactsRes, membershipsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/admin/contacts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/memberships`, { headers: getAuthHeaders() }),
      ]);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
      setMemberships(Array.isArray(membershipsRes.data) ? membershipsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Event handlers
  const openEventModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        title_de: event.title_de,
        title_it: event.title_it,
        description_de: event.description_de,
        description_it: event.description_it,
        date: event.date,
        location: event.location,
        year: event.year,
        image_url: event.image_url || '',
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        title_de: '',
        title_it: '',
        description_de: '',
        description_it: '',
        date: '',
        location: '',
        year: new Date().getFullYear(),
        image_url: '',
      });
    }
    setShowEventModal(true);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, eventForm, {
          headers: getAuthHeaders(),
        });
        toast.success(language === 'de' ? 'Event aktualisiert' : 'Evento aggiornato');
      } else {
        await axios.post(`${API}/admin/events`, eventForm, {
          headers: getAuthHeaders(),
        });
        toast.success(language === 'de' ? 'Event erstellt' : 'Evento creato');
      }
      setShowEventModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(t('form_error'));
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm(language === 'de' ? 'Event wirklich löschen?' : 'Eliminare l\'evento?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: getAuthHeaders(),
      });
      toast.success(language === 'de' ? 'Event gelöscht' : 'Evento eliminato');
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(t('form_error'));
    }
  };

  // Seed data
  const seedData = async () => {
    try {
      const response = await axios.post(`${API}/seed`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 md:p-12 w-full max-w-md luxury-shadow"
        >
          <div className="text-center mb-8">
            <img 
              src="/logo-bunt.jpg" 
              alt="Amitalia e.V." 
              className="h-24 w-auto mx-auto mb-4"
            />
            <p className="text-foreground/60 text-sm">{t('admin_login')}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="username">{t('admin_username')}</Label>
              <Input
                id="username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                data-testid="admin-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('admin_password')}</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? '...' : t('admin_login')}
            </button>
          </form>

          <p className="text-xs text-foreground/40 text-center mt-8">
            Demo: admin / amitalia2011
          </p>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="font-heading text-xl font-semibold flex items-center gap-3">
            <img 
              src="/logo-bunt.jpg" 
              alt="Amitalia e.V." 
              className="h-10 w-auto"
            />
            <span className="text-foreground/40 font-body text-sm">Admin</span>
          </h1>
          <button
            onClick={() => setIsLoggedIn(false)}
            className="flex items-center gap-2 text-foreground/60 hover:text-foreground text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} />
            {t('admin_logout')}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="events" className="space-y-8">
          <TabsList className="bg-white p-1">
            <TabsTrigger value="events" className="flex items-center gap-2" data-testid="tab-events">
              <Calendar size={16} />
              {t('admin_events')}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2" data-testid="tab-contacts">
              <Mail size={16} />
              {t('admin_contacts')}
            </TabsTrigger>
            <TabsTrigger value="memberships" className="flex items-center gap-2" data-testid="tab-memberships">
              <Users size={16} />
              {t('admin_memberships')}
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="bg-white p-6 luxury-shadow">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-semibold">{t('admin_events')}</h2>
                <div className="flex gap-2">
                  <button
                    onClick={seedData}
                    className="btn-secondary text-sm py-2 px-4"
                    data-testid="seed-data-btn"
                  >
                    Seed Demo Data
                  </button>
                  <button
                    onClick={() => openEventModal()}
                    className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                    data-testid="add-event-btn"
                  >
                    <Plus size={16} />
                    {t('admin_add_event')}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {events.length === 0 ? (
                  <p className="text-center text-foreground/60 py-8">
                    {language === 'de' ? 'Keine Events vorhanden' : 'Nessun evento'}
                  </p>
                ) : (
                  events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-center justify-between p-4 border border-black/5 hover:bg-[#F5F5F7] transition-colors"
                      data-testid={`admin-event-${event.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt=""
                            className="w-16 h-12 object-cover"
                          />
                        )}
                        <div>
                          <h3 className="font-semibold">{language === 'de' ? event.title_de : event.title_it}</h3>
                          <p className="text-sm text-foreground/60">
                            {event.date} • {event.location}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEventModal(event)}
                          className="p-2 hover:bg-white rounded"
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteEvent(event.id)}
                          className="p-2 hover:bg-white rounded text-red-500"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white p-6 luxury-shadow">
              <h2 className="font-heading text-xl font-semibold mb-6">{t('admin_contacts')}</h2>
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <p className="text-center text-foreground/60 py-8">
                    {language === 'de' ? 'Keine Kontaktanfragen' : 'Nessuna richiesta'}
                  </p>
                ) : (
                  contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-4 border border-black/5"
                      data-testid={`admin-contact-${contact.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold">{contact.name}</h3>
                          <p className="text-sm text-foreground/60">{contact.email}</p>
                        </div>
                        <span className="text-xs text-foreground/40">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm font-medium mb-1">{contact.subject}</p>
                      <p className="text-sm text-foreground/70">{contact.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Memberships Tab */}
          <TabsContent value="memberships">
            <div className="bg-white p-6 luxury-shadow">
              <h2 className="font-heading text-xl font-semibold mb-6">{t('admin_memberships')}</h2>
              <div className="space-y-4">
                {memberships.length === 0 ? (
                  <p className="text-center text-foreground/60 py-8">
                    {language === 'de' ? 'Keine Mitgliedsanträge' : 'Nessuna richiesta'}
                  </p>
                ) : (
                  memberships.map((membership) => (
                    <div
                      key={membership.id}
                      className="p-4 border border-black/5"
                      data-testid={`admin-membership-${membership.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{membership.first_name} {membership.last_name}</h3>
                          <p className="text-sm text-foreground/60">{membership.email}</p>
                          <p className="text-sm text-foreground/60">
                            {membership.address}, {membership.postal_code} {membership.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-2 py-1 text-xs ${
                            membership.status === 'approved' 
                              ? 'bg-green-100 text-green-800' 
                              : membership.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {membership.status}
                          </span>
                          <p className="text-xs text-foreground/40 mt-1">
                            {membership.membership_type}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="event-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-black/5 flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold">
                {editingEvent ? t('admin_edit_event') : t('admin_add_event')}
              </h2>
              <button onClick={() => setShowEventModal(false)} data-testid="close-modal-btn">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveEvent} className="p-6 space-y-6" data-testid="event-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Titel (DE) *</Label>
                  <Input
                    value={eventForm.title_de}
                    onChange={(e) => setEventForm({ ...eventForm, title_de: e.target.value })}
                    required
                    data-testid="event-title-de"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titolo (IT) *</Label>
                  <Input
                    value={eventForm.title_it}
                    onChange={(e) => setEventForm({ ...eventForm, title_it: e.target.value })}
                    required
                    data-testid="event-title-it"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Beschreibung (DE) *</Label>
                  <Textarea
                    value={eventForm.description_de}
                    onChange={(e) => setEventForm({ ...eventForm, description_de: e.target.value })}
                    required
                    rows={3}
                    data-testid="event-desc-de"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione (IT) *</Label>
                  <Textarea
                    value={eventForm.description_it}
                    onChange={(e) => setEventForm({ ...eventForm, description_it: e.target.value })}
                    required
                    rows={3}
                    data-testid="event-desc-it"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Datum *</Label>
                  <Input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => {
                      const date = e.target.value;
                      const year = new Date(date).getFullYear();
                      setEventForm({ ...eventForm, date, year });
                    }}
                    required
                    data-testid="event-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ort *</Label>
                  <Input
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    required
                    data-testid="event-location"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Jahr</Label>
                  <Input
                    type="number"
                    value={eventForm.year}
                    onChange={(e) => setEventForm({ ...eventForm, year: parseInt(e.target.value) })}
                    data-testid="event-year"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bild URL</Label>
                <Input
                  value={eventForm.image_url}
                  onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="event-image-url"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="btn-secondary flex-1"
                >
                  {t('admin_cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="save-event-btn"
                >
                  {loading ? '...' : t('admin_save')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Admin;
